import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiRotateCcw, FiHome, FiInfo, FiClock, FiUsers, FiCpu } = FiIcons;

const MobileGameLayout = ({ 
  children, 
  gameInfo, 
  timer, 
  onReset, 
  onGoToMenu, 
  mode, 
  currentPlayer, 
  gameStatus, 
  gameStats 
}) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  return (
    <div className="min-h-screen flex flex-col">
      {/* Mobile Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-black/30 backdrop-blur-sm border-b border-white/10 p-4 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <SafeIcon icon={mode === 'two-player' ? FiUsers : FiCpu} className="text-2xl text-blue-400" />
          <div>
            <h1 className="text-lg font-bold text-white">Chess Game</h1>
            <p className="text-sm text-gray-300 capitalize">{mode} Mode</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Current Player Indicator */}
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
            currentPlayer === 'white' 
              ? 'bg-white text-black' 
              : 'bg-gray-800 text-white border border-white/20'
          }`}>
            {currentPlayer === 'white' ? '♔' : '♛'} {currentPlayer}
          </div>
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowSidebar(true)}
            className="p-2 bg-white/10 rounded-lg text-white"
          >
            <SafeIcon icon={FiMenu} className="text-xl" />
          </motion.button>
        </div>
      </motion.header>

      {/* Game Status Bar */}
      <div className="bg-black/20 backdrop-blur-sm border-b border-white/10 px-4 py-2">
        {gameStatus === 'check' && (
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="text-center py-2 bg-red-500/20 border border-red-500/50 rounded-lg"
          >
            <p className="text-red-300 font-semibold">CHECK!</p>
          </motion.div>
        )}
        
        {gameStatus === 'playing' && (
          <div className="text-center">
            <p className="text-gray-300 text-sm">
              {currentPlayer === 'white' ? 'White' : 'Black'} to move
            </p>
          </div>
        )}
      </div>

      {/* Main Game Area */}
      <div className="flex-1 p-4">
        {children}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="bg-black/30 backdrop-blur-sm border-t border-white/10 p-2">
        <div className="flex justify-around">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onReset}
            className="flex flex-col items-center gap-1 p-2 rounded-lg text-blue-400"
          >
            <SafeIcon icon={FiRotateCcw} className="text-xl" />
            <span className="text-xs">Reset</span>
          </motion.button>
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowSidebar(true)}
            className="flex flex-col items-center gap-1 p-2 rounded-lg text-green-400"
          >
            <SafeIcon icon={FiInfo} className="text-xl" />
            <span className="text-xs">Info</span>
          </motion.button>
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onGoToMenu}
            className="flex flex-col items-center gap-1 p-2 rounded-lg text-gray-400"
          >
            <SafeIcon icon={FiHome} className="text-xl" />
            <span className="text-xs">Menu</span>
          </motion.button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={() => setShowSidebar(false)}
            />
            
            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="fixed right-0 top-0 h-full w-80 max-w-[90vw] bg-slate-900 z-50 flex flex-col"
            >
              {/* Sidebar Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <h2 className="text-xl font-bold text-white">Game Info</h2>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowSidebar(false)}
                  className="p-2 text-gray-400 hover:text-white"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </motion.button>
              </div>

              {/* Tab Navigation */}
              <div className="flex border-b border-white/10">
                {[
                  { id: 'info', label: 'Info', icon: FiInfo },
                  { id: 'timer', label: 'Timer', icon: FiClock }
                ].map((tab) => (
                  <motion.button
                    key={tab.id}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 ${
                      activeTab === tab.id 
                        ? 'bg-blue-600 text-white' 
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <SafeIcon icon={tab.icon} />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </motion.button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 'info' && (
                  <div className="p-4 space-y-6">
                    {gameInfo}
                  </div>
                )}
                
                {activeTab === 'timer' && (
                  <div className="p-4 space-y-6">
                    {timer}
                    
                    {/* Game Stats */}
                    <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                      <h3 className="text-white font-semibold mb-3">Game Stats</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Moves:</span>
                          <span className="text-white">{gameStats?.totalMoves || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Checks:</span>
                          <span className="text-yellow-400">{gameStats?.checksDelivered || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Captures:</span>
                          <span className="text-purple-400">{gameStats?.piecesCaptures || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar Actions */}
              <div className="p-4 border-t border-white/10 space-y-2">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onReset();
                    setShowSidebar(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                >
                  <SafeIcon icon={FiRotateCcw} />
                  Reset Game
                </motion.button>
                
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onGoToMenu();
                    setShowSidebar(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold"
                >
                  <SafeIcon icon={FiHome} />
                  Main Menu
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileGameLayout;